'use client';
import { cn } from '@/utils/styles';
import {
  FormItem,
  FormField,
  FormControl,
  FormMessage,
  useFormField,
  FormDescription,
} from '../Form';

import { Select } from '../Select';
import * as RadioGroup from '@radix-ui/react-radio-group';

type TOptionValuePrimitive = string | number | boolean | undefined;

export type TOption = {
  label: React.ReactNode;
  label2: React.ReactNode;
  label3: React.ReactNode;
  value: TOptionValuePrimitive;
};

export type SelectFieldProps = React.ComponentProps<typeof Select> &
  Omit<
    React.HTMLAttributes<HTMLButtonElement>,
    'defaultValue' | 'dir' | 'value'
  > & {
    name: string;
    options?: TOption[];
    placeholder?: string;
    label?: React.ReactNode;
    description?: React.ReactNode;
    itemsClass?: string;
    labelsClassName?: string;
  } & (
    | {
        multiple: true;
        value?: TOptionValuePrimitive[];
      }
    | {
        multiple?: false;
        value?: TOptionValuePrimitive;
      }
  );

export const RadioGroupField = ({
  name,
  label,
  options,
  className,
  description,
  itemsClass = 'h-10',
  labelsClassName,
}: SelectFieldProps) => {
  const formField = useFormField();

  return (
    <FormField
      name={name}
      control={formField.control}
      render={({ field }) => {
        return (
          <FormItem className={className}>
            <FormControl>
              <div className="rounded-md">
                <p className="text-sm font-medium text-gray-700 mb-2 px-5">
                  {label}
                </p>
                <RadioGroup.Root
                  className="flex flex-col gap-2"
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                >
                  {options?.map((option) => (
                    <RadioGroup.Item
                      key={String(option.value)}
                      value={String(option.value)}
                      id={String(option.value)}
                      className={cn(
                        'flex items-center justify-between p-5 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white gap-2',
                        itemsClass,
                      )}
                    >
                      <div className="flex flex-col">
                        <label
                          htmlFor={String(option.value)}
                          className={cn(
                            'text-sm font-medium text-gray-900',
                            labelsClassName,
                          )}
                        >
                          {option.label}
                        </label>
                        {option.label2 && (
                          <label
                            htmlFor={String(option.value)}
                            className={cn(
                              'text-sm font-medium text-tertiary',
                              labelsClassName,
                            )}
                          >
                            {option.label2}
                          </label>
                        )}
                        {option.label3 && (
                          <label
                            htmlFor={String(option.value)}
                            className={cn(
                              'text-sm font-medium text-tertiary',
                              labelsClassName,
                            )}
                          >
                            {option.label3}
                          </label>
                        )}
                      </div>

                      <div className="flex-shrink-0">
                        <div
                          className={cn(
                            'w-4 h-4 rounded-full flex items-center justify-center border border-solid',
                            field.value === String(option.value)
                              ? 'bg-black border-black'
                              : 'border-gray-400',
                          )}
                        >
                          {field.value === String(option.value) && (
                            <div className="w-1 h-1 rounded-full bg-white" />
                          )}
                        </div>
                      </div>
                    </RadioGroup.Item>
                  ))}
                </RadioGroup.Root>
              </div>
            </FormControl>

            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
